import { useState } from 'react';
import { useQuery } from 'urql';
import { queryServicesQuery } from '../../graphql/services';
import { Category } from './useCategorySelection';

interface ImageLoadingState {
  [key: number]: boolean;
}

export interface Service {
  id: string;
  name: string;
  price: number;
  description: string;
  duration: number;
  isVideoService: boolean;
  image: {
    secure_url: string;
  };
}

interface UseServiceSelectionProps {
  selectedServices: Service[];
  setSelectedServices: React.Dispatch<React.SetStateAction<Service[]>>;
  itemsPerPage: number;
  selectedCategories: Category[];
}

interface UseServiceSelectionReturn {
  imageLoading: ImageLoadingState;
  currentCursor: string | null;
  direction: 'forward' | 'backward';
  expandedDescriptionId: string | null;
  queryResult: any; // Replace 'any' with actual query result type
  handleImageLoad: (serviceId: number) => void;
  handleImageLoadStart: (serviceId: number) => void;
  toggleService: (service: Service) => void;
  handlePageChange: (direction: 'next' | 'previous') => void;
  setExpandedDescriptionId: (id: string | null) => void;
  formatDuration: (minutes: number) => string;
  shouldShowReadMore: (text: string) => boolean;
}

const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 50;

export function useServiceSelection({
  selectedServices,
  setSelectedServices,
  itemsPerPage,
  selectedCategories
}: UseServiceSelectionProps): UseServiceSelectionReturn {
  const [imageLoading, setImageLoading] = useState<ImageLoadingState>({});
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [expandedDescriptionId, setExpandedDescriptionId] = useState<string | null>(null);

  const paginationVariables = {
    filter: {
      isDeleted: { is: false },
       isActive: { is: true},
      ...(selectedCategories.length > 0 && {
        categories: {
          id: {
            in: selectedCategories.map(category => category.id)
          }
        }
      })
    },
    paging: direction === 'forward'
      ? { first: itemsPerPage, after: currentCursor }
      : { last: itemsPerPage, before: currentCursor },
  };

  const [queryResult] = useQuery({
    query: queryServicesQuery,
    variables: currentCursor
      ? paginationVariables
      : {
          filter: {
            isDeleted: { is: false },
            isActive: { is: true},
            ...(selectedCategories.length > 0 && {
              categories: {
                id: {
                  in: selectedCategories.map(category => category.id)
                }
              }
            })
          },
          paging: { first: itemsPerPage }
        },
  });

  const toggleService = (service: Service) => {
    setSelectedServices((prev) =>
      prev.some((s) => s.id === service.id)
        ? prev.filter((s) => s.id !== service.id)
        : [...prev, service]
    );
  };

  const handleImageLoad = (serviceId: number) => {
    setImageLoading((prev) => ({
      ...prev,
      [serviceId]: false,
    }));
  };

  const handleImageLoadStart = (serviceId: number) => {
    setImageLoading((prev) => ({
      ...prev,
      [serviceId]: true,
    }));
  };

  const handleNextPage = () => {
    if (queryResult.data?.domainServices.pageInfo.hasNextPage) {
      setDirection('forward');
      setCurrentCursor(queryResult.data.domainServices.pageInfo.endCursor);
    }
  };

  const handlePreviousPage = () => {
    if (queryResult.data?.domainServices.pageInfo.hasPreviousPage) {
      setDirection('backward');
      setCurrentCursor(queryResult.data.domainServices.pageInfo.startCursor);
    }
  };

  const handlePageChange = (direction: 'next' | 'previous') => {
    if (direction === 'next') {
      handleNextPage();
    } else {
      handlePreviousPage();
    }
  };

  const formatDuration = (seconds: number) => {
    // Convert seconds to minutes first
    const totalMinutes = Math.floor(seconds / 60);

    if (totalMinutes < 60) {
      return `${totalMinutes}min`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      return remainingMinutes > 0
        ? `${hours}h ${remainingMinutes}min`
        : `${hours}h`;
    }
  };

  const shouldShowReadMore = (text: string) => {
    return text.length > DESCRIPTION_MAX_LENGTH;
  };

  return {
    imageLoading,
    currentCursor,
    direction,
    expandedDescriptionId,
    queryResult,
    handleImageLoad,
    handleImageLoadStart,
    toggleService,
    handlePageChange,
    setExpandedDescriptionId,
    formatDuration,
    shouldShowReadMore,
  };
}
